import {useMemo} from "react";
import cn from "classnames";
import "./index.css";
import "../../../layout/BannerToolbar/index.css";
import BgTransformer from "../../../layout/BgTransformer";
import PixelationLayer from "../../../layout/PixelationLayer";
import CodeHighlight from "../../../layout/CodeHighlight";
import {useSetting} from "../../../contexts/setting";
import {useImages} from "../../../contexts/images";
import {useBgUrl} from "hooks/useBgUrl";
import {getTemplateMetadata} from "../../../data/templateManifest";
import {codeHighlightOf} from "libs/codeHighlight";
import type {CodeHighlight as CodeHighlightValue} from "libs/codeHighlight";
import {DemoVideoScreenshot} from "./parts";

/** The tool icon: a rounded box with the outline of the highlight. */
const HighlightIcon = () => (
  <svg width="22" height="14" viewBox="0 0 22 14" fill="none" aria-hidden="true">
    <rect x="1.5" y="1.5" width="19" height="11" rx="3" fill="white" fillOpacity="0.2" stroke="white" strokeWidth="2" />
  </svg>
);

/**
 * Code Demo (Figma "release/code-demo/highlight" 12710:1550): a screenshot of
 * code on the whole banner and the code highlight above it
 * (layout/CodeHighlight). The highlight is a layer of its own, not a part of
 * the screenshot: the tools above the banner show or hide it and give its
 * colour; drag it on the banner to move it or resize it. The screenshot
 * covers the background (the Demo artwork) once there is one.
 */
const DemoVideoCode = ({id, zoom}: {id: string; zoom: number}) => {
  const {product, isControlledBanner} = useSetting();
  const {url: bgUrl} = useBgUrl();
  const {codeHighlights, editCodeHighlight} = useImages() as unknown as {
    codeHighlights: Record<string, CodeHighlightValue>;
    editCodeHighlight?: (id: string, value: CodeHighlightValue) => void;
  };
  const highlight = codeHighlightOf(codeHighlights, id, product.value);
  const {width, height} = getTemplateMetadata("releases", "DemoVideoCode", product.value);

  const change = useMemo(() => {
    // Grid renders the design default; its document has no highlight state.
    if (isControlledBanner) return undefined;
    if (typeof editCodeHighlight !== "function") throw new Error("Code highlight editor requires a writer");
    return (next: CodeHighlightValue) => editCodeHighlight(id, next);
  }, [id, editCodeHighlight, isControlledBanner]);

  return (
    <div
      id={id}
      className={cn("template", product.value, "demo-video", "demo-video-code")}
      style={{"--zoom-level": zoom} as React.CSSProperties}
    >
      {/* Template layers: the export draws the first child only. */}
      <div>
        <BgTransformer src={bgUrl} id={id} />
        <PixelationLayer />
        <DemoVideoScreenshot id={id} />
        <CodeHighlight highlight={highlight} canvas={{width, height}} onChange={change} />
      </div>

      <div className="screenshot-toolbar">
        <button
          title={!change ? "Code highlight is fixed in Grid" : highlight.enabled ? "Hide code highlight" : "Show code highlight"}
          className={cn("stb-btn", {active: highlight.enabled})}
          aria-pressed={highlight.enabled}
          disabled={!change}
          onClick={change ? () => change({...highlight, enabled: !highlight.enabled}) : undefined}
        >
          <HighlightIcon />
        </button>
        {change && highlight.enabled && (
          <label className="stb-btn dv-color-tool" title="Code highlight colour">
            <span className="dv-color-swatch" style={{backgroundColor: highlight.color}} />
            <input
              type="color"
              value={highlight.color.toLowerCase()}
              onChange={(event) => change({...highlight, color: event.target.value.toUpperCase()})}
            />
          </label>
        )}
      </div>
    </div>
  );
};

export default DemoVideoCode;
